import React, { memo, useCallback, useMemo } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import lockedIconUrl from "../../../img/trade-interface/locked.svg";
import unlockedIconUrl from "../../../img/trade-interface/unlocked.svg";
import DisabledIcon from "../../../img/trade-interface/disabledlock.svg?react";
import { FlexContainer } from "../../../containers/FlexContainer";
import { AnimatePresence, motion } from "framer-motion";
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { Hash } from "viem";
import { isAddressEqual } from "../../../utils/tokenUtils";

export interface LockButtonProps {
  tokenAddress: Hash;
  canLock: boolean;
  isLocked: boolean;
  size: number;
  iconSize?: number;
  onClick?: () => void;
}

export const LockButton = (props: LockButtonProps) => {
  const tokenList = useTokenListByChain();
  const lockBgColour = tokenList?.[props.tokenAddress]?.colour ?? "#CCC";
  const { isLocked, theme } = GlobalStore.useState(s => ({
    isLocked: s.makerTokens.find(makerToken =>
      isAddressEqual(makerToken.tokenAddress, props.tokenAddress)
    )?.isLocked,
    theme: s.theme,
  }));

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      const setIsLocked = (newIsLockedState: boolean) =>
        GlobalStore.update(s => {
          const currentTokenState = s.makerTokens.find(makerToken =>
            isAddressEqual(makerToken.tokenAddress, props.tokenAddress)
          );
          if (currentTokenState != null) {
            currentTokenState.isLocked = newIsLockedState;
          }
        });

      if (!props.canLock && !props.isLocked) return;
      if (props.onClick != null) props.onClick();
      if (!isLocked) {
        setIsLocked(true);
      } else {
        setIsLocked(false);
      }
    },
    [isLocked, props]
  );
  const isDisabled = !isLocked && !props.canLock;
  const backgroundColor = useMemo(() => {
    if (isDisabled) return theme.button.primary.surface.disabled;
    return isLocked ? lockBgColour : "transparent";
  }, [isDisabled, isLocked, lockBgColour, theme.button.primary.surface.disabled]);

  const LockIcon = memo(() => {
    if (isDisabled) {
      return (
        <DisabledIcon
          key="disabled"
          data-testid={`${props.tokenAddress + "-"}lock-btn`}
          width={18}
        />
      );
    }
    return (
      <motion.img
        width={props.iconSize ?? "initial"}
        src={isLocked ? lockedIconUrl : unlockedIconUrl}
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.8 }}
        data-testid={`${props.tokenAddress + "-"}lock-btn`}
      />
    );
  });
  return (
    <FlexContainer onClick={e => handleOnClick(e)}>
      {" "}
      <LockedIconContainer
        isDisabled={isDisabled}
        showBorder={!isLocked && props.canLock}
        size={props.size}
        initial={{ backgroundColor: "none" }}
        animate={{ backgroundColor }}
        transition={{ duration: 0.3, ease: "easeIn" }}
      >
        <AnimatePresence>
          <LockIcon />
        </AnimatePresence>
      </LockedIconContainer>
    </FlexContainer>
  );
};

const LockedIconContainer = styled(motion.button)<{
  showBorder: boolean;
  size: number;
  isDisabled: boolean;
}>`
  display: flex;
  padding: 2px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border: 1px solid ${props => (props.showBorder ? props.theme.theme[1200] : "transparent")};
  border-radius: 18px;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  cursor: ${props => (props.isDisabled ? "not-allowed!important" : "inherit")};
`;
